import type { InTransactionFormData, OtherAmountRow } from './InTransactionSections';
import type { OutTransactionFormData } from './OutTransactionSections';

/** documentId of a populated relation object (or the raw value if already an id) */
const relDocId = (v: any): string => {
  if (v && typeof v === 'object') return v.documentId || String(v.id || '');
  return v ? String(v) : '';
};

const toDateInput = (v?: string | null): string => (v ? String(v).split('T')[0] : '');

/** Map a saved transaction record to In Transaction form values (edit mode) */
export function transactionToInFormValues(t: any): InTransactionFormData {
  return {
    invoiceId: relDocId(t.invoiceHire || t.invoice),
    amount: Number(t.amount) || 0,
    currency: relDocId(t.currency),
    exchangeRate: t.exchangeRate ?? undefined,
    amountCollectedCurrency: relDocId(t.amountCollectedCurrency),
    amountCollectedRate: t.amountCollectedRate ?? undefined,
    chequeDate: toDateInput(t.chequeDate),
    chequeNumber: t.chequeNumber || '',
    bankName: t.bankName || '',
    depositAccount: t.bank ? `bank:${relDocId(t.bank)}` : t.safe ? `safe:${relDocId(t.safe)}` : '',
    deductionAmount: t.deductionAmount ?? undefined,
    deductionCurrency: relDocId(t.deductionCurrency),
    deductionRate: t.deductionRate ?? undefined,
    notes: t.notes || '',
    transactionDate: toDateInput(t.date) || new Date().toISOString().split('T')[0],
  };
}

/** Map a saved transaction's otherAmounts JSON back to form rows (edit mode) */
export function transactionToInOtherAmounts(t: any): OtherAmountRow[] {
  if (!Array.isArray(t.otherAmounts)) return [];
  return t.otherAmounts.map((oa: any, idx: number) => ({
    id: `saved-${idx}`,
    amount: Number(oa.amount) || 0,
    currency: relDocId(oa.currency),
    exchangeRate: oa.exchangeRate ?? undefined,
    chequeDate: toDateInput(oa.chequeDate),
    chequeNumber: oa.chequeNumber || '',
    bankName: oa.bankName || '',
    depositAccount: oa.accountType && oa.accountId ? `${oa.accountType}:${oa.accountId}` : '',
  }));
}

/** Map a saved transaction record to Out Transaction form values (edit mode) */
export function transactionToOutFormValues(t: any): Partial<OutTransactionFormData> {
  return {
    description: t.description || '',
    amount: Number(t.amount) || 0,
    currency: relDocId(t.currency) || 'EGP',
    exchangeRate: t.exchangeRate ?? undefined,
    amountToType: t.safe ? 'safe' : 'bank',
    bankId: t.bank ? relDocId(t.bank) : '',
    safeId: t.safe ? relDocId(t.safe) : '',
    categoryId: relDocId(t.category),
    branchId: relDocId(t.branch),
    recipientType: t.recipientType === 'supplier' ? 'supplier' : 'employee',
    employeeId: t.recipientType === 'employee' ? (t.recipientId || '') : '',
    supplierId: t.recipientType === 'supplier' ? (t.recipientId || '') : '',
    billId: relDocId(t.bill),
    notes: t.notes || '',
    moneyIn: false,
    transferType: t.chequeNumber ? 'check' : 'transfer',
    checkNumber: t.chequeNumber || '',
    transactionDate: toDateInput(t.date) || new Date().toISOString().split('T')[0],
  };
}
